// Component: TemplateForm
// Purpose: Simplified admin form for creating/editing a graphics template.
// Only requires: Canva link, Tags, Search Text, and Preview Image.
// Type defaults to "graphic".
import React, { useEffect, useState } from 'react';
import { TAG_LIST } from '../../../data/tags/tagEnums';
import { ButtonSpinner } from '../../../components/ui/feedback/loading-spinner.jsx';
import ImageUploader from './ImageUploader';

const TemplateForm = ({ onSubmit, isSubmitting, initialData, submitButtonText = 'Create Template' }) => {
  const [formData, setFormData] = useState({
    canvaLink: '',
    tags: [],
    searchableText: ''
  });
  const [previewImage, setPreviewImage] = useState(null);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  
  // For URL input values without protocols
  const [urlInputValues, setUrlInputValues] = useState({
    canvaLink: ''
  });

  // Effect to apply initialData if provided
  useEffect(() => {
    if (initialData) {
      const newFormData = {
        canvaLink: '',
        tags: [],
        searchableText: ''
      };
      const newUrlInputValues = { canvaLink: '' };
      
      if (initialData.canvaLink) {
        newFormData.canvaLink = initialData.canvaLink;
        newUrlInputValues.canvaLink = initialData.canvaLink.replace(/^https?:\/\/(www\.)?/, '');
      }
      
      // Handle tags: could be array or comma-separated string
      if (initialData.tags) {
        if (Array.isArray(initialData.tags)) {
          newFormData.tags = initialData.tags;
        } else if (typeof initialData.tags === 'string') {
          newFormData.tags = initialData.tags.split(',').map(tag => tag.trim());
        }
        
        // Filter tags to match those in TAG_LIST
        newFormData.tags = newFormData.tags.filter(tag => 
          TAG_LIST.includes(tag) || TAG_LIST.find(t => t.toLowerCase() === tag.toLowerCase())
        );
      }
      
      // Handle searchableText
      if (initialData.searchableText) {
        newFormData.searchableText = initialData.searchableText;
      } else if (initialData.keywords) {
        // Fallback: convert keywords array to searchableText string for backwards compatibility
        if (Array.isArray(initialData.keywords)) {
          newFormData.searchableText = initialData.keywords.join(' ');
        } else if (typeof initialData.keywords === 'string') {
          newFormData.searchableText = initialData.keywords;
        }
      }
      
      // Update state
      setFormData(newFormData);
      setUrlInputValues(newUrlInputValues);
      
      // Mark all fields as touched
      const newTouched = Object.keys(newFormData).reduce((acc, key) => {
        if (newFormData[key] && 
            (typeof newFormData[key] === 'string' ? newFormData[key].trim() !== '' : true)) {
          acc[key] = true;
        }
        return acc;
      }, {});
      
      setTouched(newTouched);
    }
  }, [initialData]);

  useEffect(() => {
    // Initialize input values from form data if they have values
    if (formData.canvaLink && formData.canvaLink !== 'https://') {
      setUrlInputValues(prev => ({
        ...prev,
        canvaLink: formData.canvaLink.replace(/^https?:\/\/(www\.)?/, ''),
      }));
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Handle URL inputs differently
    if (name === 'canvaLink') {
      handleUrlChange(name, value);
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
    
    // Mark field as touched
    if (!touched[name]) {
      setTouched({
        ...touched,
        [name]: true,
      });
    }
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
  };
  
  const handleUrlChange = (name, value) => {
    // Process URL: remove any protocol and www prefix
    const processedValue = value.replace(/^(https?:\/\/)?(www\.)?/, '');
    
    // Update the input value state (without protocol)
    setUrlInputValues({
      ...urlInputValues,
      [name]: processedValue,
    });
    
    // Update form data with https:// prefix
    setFormData({
      ...formData,
      [name]: processedValue ? `https://${processedValue}` : '',
    });
  };

  const handleTagToggle = (tag) => {
    const newTags = formData.tags.includes(tag)
      ? formData.tags.filter(t => t !== tag)
      : [...formData.tags, tag];
    
    setFormData({ ...formData, tags: newTags });
    
    // Mark the field as touched
    if (!touched.tags) {
      setTouched({ ...touched, tags: true });
    }
    
    // If there was an error and now we have tags, clear the error
    if (errors.tags && newTags.length > 0) {
      setErrors({ ...errors, tags: '' });
    }
  };

  const handleImageChange = (file) => {
    console.log('handleImageChange called with file:', file);
    
    // Check if this is an existing image (for edit mode)
    if (file && file.isExistingImage) {
      setPreviewImage(file);
      console.log('Set existing image as preview:', file);
    } else {
      setPreviewImage(file);
      console.log('Set new uploaded file as preview:', file);
    }
    
    // Mark as touched
    if (!touched.previewImage) {
      setTouched({
        ...touched,
        previewImage: true,
      });
    }
    // Clear error
    if (errors.previewImage) {
      setErrors({
        ...errors,
        previewImage: '',
      });
    }
  };

  const validateForm = () => {
    console.log('[TemplateForm] Validating form with data:', {
      canvaLink: formData.canvaLink,
      tags: formData.tags,
      searchableText: formData.searchableText,
    });
    console.log('[TemplateForm] Preview image:', previewImage ? (previewImage.isExistingImage ? 'Existing image' : 'New image file') : 'Missing');
    
    const newErrors = {};
    
    // Validate Canva link
    if (!formData.canvaLink || formData.canvaLink === 'https://') {
      newErrors.canvaLink = 'Canva template link is required';
    }
    
    if (formData.tags.length === 0) {
      newErrors.tags = 'At least one tag is required';
    }
    
    // Check preview image
    if (!previewImage) {
      newErrors.previewImage = 'Please upload an image of the template';
    }
    
    console.log('[TemplateForm] Validation errors:', newErrors);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isFormComplete = () => {
    // For edit mode with existing image, check if we have an isExistingImage flag
    const hasValidImage = previewImage !== null && 
      (typeof previewImage === 'object' && 
       (previewImage instanceof File || previewImage.isExistingImage));
    
    return formData.canvaLink && 
           formData.canvaLink !== 'https://' &&
           formData.tags.length > 0 &&
           hasValidImage;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // Update touched state for all fields to show all errors
      const allTouched = Object.keys(formData).reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {});
      allTouched.previewImage = true;
      setTouched(allTouched);
      
      console.error('Form validation failed', errors);
      return;
    }
    
    console.log('Submitting form with data:', formData);
    console.log('Preview image:', previewImage);
    
    // Create FormData object for submission (supports file uploads)
    const formDataObj = new FormData();
    
    // Add fields to FormData
    formDataObj.append('canvaLink', formData.canvaLink);
    formDataObj.append('tags', formData.tags.join(','));
    
    // Send searchableText if provided
    if (formData.searchableText && formData.searchableText.trim()) {
      formDataObj.append('searchableText', formData.searchableText.trim());
    }
    
    // Preserve the template's type when editing (e.g. mail); new templates default to graphic
    formDataObj.append('type', initialData?.type || 'graphic');
    
    // Add the preview image
    if (previewImage && !previewImage.isExistingImage) {
      formDataObj.append('previewImage', previewImage);
    } else if (previewImage && previewImage.isExistingImage) {
      // Tell backend to keep the existing image
      formDataObj.append('keepExistingImage', 'true');
    }
    
    // Pass FormData to parent component's onSubmit
    onSubmit(formDataObj);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-7">
      {/* Canva Design Link */}
      <div>
        <label htmlFor="canvaLink" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Canva Template Link
        </label>
        <div className="mt-1 flex rounded-md shadow-sm">
          <span className="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-50 px-3 text-gray-500 sm:text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
            https://
          </span>
          <input
            type="text"
            id="canvaLink"
            name="canvaLink"
            value={urlInputValues.canvaLink}
            onChange={handleChange}
            className={`block w-full flex-1 rounded-none rounded-r-md px-3 py-2 border-gray-300 focus:border-primary-500 focus:ring-primary-500 sm:text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100
              ${errors.canvaLink && touched.canvaLink ? 'border-red-300 dark:border-red-700 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
            placeholder="canva.com/design/..."
          />
        </div>
        {errors.canvaLink && touched.canvaLink && (
          <p className="mt-2 text-sm text-red-600 dark:text-red-500 ml-1" id="canvaLink-error">
            {errors.canvaLink}
          </p>
        )}
      </div>
      
      {/* Tags */}
      <div>
        <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Tags
        </span>
        <div className="mt-2">
          <div className="flex flex-wrap gap-2">
            {TAG_LIST.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => handleTagToggle(tag)}
                className={`inline-flex items-center rounded-full px-3 py-1 text-sm
                  ${formData.tags.includes(tag) 
                    ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                  }`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
        {errors.tags && touched.tags && (
          <p className="mt-2 text-sm text-red-600 dark:text-red-500 ml-1">
            {errors.tags}
          </p>
        )}
      </div>
      
      {/* Search Text */}
      <div>
        <label htmlFor="searchableText" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Search Text (Optional)
        </label>
        <div className="mt-1">
          <input
            type="text"
            id="searchableText"
            name="searchableText"
            value={formData.searchableText || ''}
            onChange={(e) => {
              setFormData({ ...formData, searchableText: e.target.value });
              
              // Mark the field as touched
              if (!touched.searchableText) {
                setTouched({ ...touched, searchableText: true });
              }
              
              // Clear error when field is edited
              if (errors.searchableText) {
                setErrors({ ...errors, searchableText: '' });
              }
            }}
            className={`block w-full rounded-md px-3 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm
              ${errors.searchableText && touched.searchableText ? 'border-red-300 dark:border-red-700 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100'}`}
            placeholder="john fetterman congress pennsylvania senate campaign"
          />
        </div>
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 ml-1">
          Add search terms that describe the template content, people, places, or topics. This will make your template easily discoverable. Tags are automatically included.
        </p>
        {errors.searchableText && touched.searchableText && (
          <p className="mt-2 text-sm text-red-600 dark:text-red-500 ml-1" id="searchableText-error">
            {errors.searchableText}
          </p>
        )}
      </div>
      
      {/* Preview Image */}
      <div>
        <label htmlFor="previewImage" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Template Preview
        </label>
        <div className="mt-1">
          <ImageUploader 
            onImageChange={handleImageChange} 
            error={errors.previewImage && touched.previewImage ? errors.previewImage : ''} 
            initialImage={initialData?.previewImage || null} 
          />
        </div>
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 ml-1">
          Upload an image showing the template design
        </p>
      </div>
      
      {/* Submit Button */}
      <div className="pt-6 flex justify-end">
        <button
          type="submit"
          disabled={isSubmitting}
          className={`flex items-center justify-center py-2.5 px-5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
        >
          {isSubmitting ? (
            <>
              <ButtonSpinner color="white" />
              Submitting...
            </>
          ) : (
            submitButtonText
          )}
        </button>
      </div>
    </form>
  );
};

export default TemplateForm;
